/**
 * Lift2Fit — admin: Staff (`#/admin/staff`, migration 0038).
 *
 * The support team is the founder's answer to "I cannot answer every help chat
 * myself". This page is where the team is run:
 *
 *   * the headline numbers for the whole desk (started, served, waiting, resolved,
 *     how fast, when it peaks), and the ONE alert that matters — the queue has
 *     people in it and nobody is available;
 *   * the AI assistant as a staff member: enabled or disabled in one click, with
 *     its own handled/resolved counts;
 *   * every agent with their contract shift, signed-on state, live chat load and
 *     performance, plus add/remove and per-agent detail.
 *
 * Nothing here is inferred: the numbers come from `support_sessions`, the shift
 * window decides who can be assigned, and the bot is a row in the same table.
 */
import { useEffect, useState } from 'react';
import { api, type SupportStaffDetail, type SupportStaffView, type SupportStaffList } from '@/lib/api';
import { cn } from '@/lib/format';
import { useAsync } from '../hooks';
import { AlertIcon, CheckIcon, ShieldIcon } from '@/lib/icons';
import {
  AppLinkButton,
  EmptyState,
  Field,
  LiveMessage,
  LoadingPanel,
  Panel,
  ScreenHeader,
  StatusChip,
} from '../components';
import { ThemeToggle } from '@/components/ThemeToggle';
import { ADMIN_EYEBROW, AdminStat } from './shared';

/** Seconds as a short human duration: `—`, `45s`, `2m 10s`, `1h 4m`. */
function duration(value: number | null): string {
  if (value === null || Number.isNaN(value)) return '—';
  if (value < 60) return `${Math.round(value)}s`;
  const minutes = Math.floor(value / 60);
  const seconds = Math.round(value % 60);
  if (minutes < 60) return seconds === 0 ? `${minutes}m` : `${minutes}m ${seconds}s`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${hours}h` : `${hours}h ${rest}m`;
}

function minuteValue(text: string): number | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(text.trim());
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 24 || minutes > 59) return null;
  const total = hours * 60 + minutes;
  return total >= 0 && total <= 1440 ? total : null;
}

export function AdminStaffScreen() {
  const state = useAsync(() => api.adminStaff(), []);
  const [list, setList] = useState<SupportStaffList | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<SupportStaffDetail | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [tone, setTone] = useState<'info' | 'success' | 'danger'>('info');
  const [adding, setAdding] = useState(false);
  const [email, setEmail] = useState('');
  const [shiftStart, setShiftStart] = useState('09:00');
  const [shiftEnd, setShiftEnd] = useState('17:00');
  const [capacity, setCapacity] = useState('5');

  // The loader is authoritative; the local copy is what the buttons rewrite, so
  // a toggle updates the page without a full-screen reload flash.
  useEffect(() => {
    if (state.data) setList(state.data);
  }, [state.data]);

  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      return;
    }
    let alive = true;
    void api.adminStaffMember(selectedId).then((result) => {
      if (alive && result.data) setDetail(result.data);
    });
    return () => {
      alive = false;
    };
  }, [selectedId]);

  const refresh = async () => {
    const next = await api.adminStaff();
    if (next.data) setList(next.data);
    if (selectedId) {
      const one = await api.adminStaffMember(selectedId);
      if (one.data) setDetail(one.data);
    }
  };

  const run = async (
    label: string,
    action: () => Promise<{ data: unknown | null; problem?: { detail?: string } | null }>,
  ) => {
    setBusy(true);
    setNotice(null);
    const result = await action();
    setBusy(false);
    if (result.problem) {
      setTone('danger');
      setNotice(result.problem.detail ?? `Could not ${label}.`);
      return false;
    }
    await refresh();
    setTone('success');
    setNotice(`Done — ${label}.`);
    return true;
  };

  const addStaff = async () => {
    const start = minuteValue(shiftStart);
    const end = minuteValue(shiftEnd);
    if (start === null || end === null || start >= end) {
      setTone('danger');
      setNotice('A shift needs a start before its end, both as HH:MM.');
      return;
    }
    const created = await run(`add ${email}`, () =>
      api.addSupportStaff({
        email: email.trim(),
        shiftStartMinute: start,
        shiftEndMinute: end,
        capacity: Number(capacity) || 5,
        timezone: 'Europe/London',
        active: true,
        onShift: false,
      }),
    );
    if (created) {
      setEmail('');
      setAdding(false);
    }
  };

  if (state.loading && !list) {
    return <LoadingPanel label="Reading the support team" rows={4} />;
  }
  if (!list) {
    return (
      <EmptyState
        title="The staff page did not load"
        body={state.error ?? 'The admin API did not answer.'}
        icon={<AlertIcon className="h-8 w-8 text-accent" />}
      />
    );
  }

  const { summary, bot } = list;
  const agents = list.data.filter((entry) => !entry.isBot);

  return (
    <div className="flex flex-col gap-6" data-testid="admin-staff">
      <ScreenHeader
        eyebrow={ADMIN_EYEBROW}
        title="Staff"
        lead="The support team behind Help: who is on, who is free, how fast the desk answers — and the assistant that takes the first pass when it is enabled."
        badge={
          <StatusChip tone={summary.botEnabled ? 'accent' : 'neutral'}>
            {summary.botEnabled ? 'Assistant on' : 'Assistant off'}
          </StatusChip>
        }
        actions={<ThemeToggle />}
      />

      {summary.staffingAlert ? (
        <div
          className="flex flex-wrap items-center gap-2 rounded-chat border border-warning bg-page-subtle px-4 py-3"
          role="alert"
          data-testid="staff-alert"
        >
          <AlertIcon className="text-warning" />
          <span className="text-[13px] font-bold text-ink">
            {summary.waiting} chat{summary.waiting === 1 ? '' : 's'} waiting and nobody is available.
          </span>
          <span className="text-[12px] text-ink-muted">
            Sign someone on, or enable the assistant, or the queue keeps growing.
          </span>
        </div>
      ) : null}

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <AdminStat
          label={`Help started · ${summary.windowDays}d`}
          value={summary.started}
          hint="sessions opened in the window"
          valueTestId="staff-started"
        />
        <AdminStat
          label="Served"
          value={summary.served}
          hint="assigned to a person or the assistant"
          valueTestId="staff-served"
        />
        <AdminStat
          label="Waiting now"
          value={summary.waiting}
          hint={summary.waiting === 0 ? 'nobody in the queue' : 'nobody free when they wrote'}
          tone={summary.waiting > 0 ? 'text-warning' : 'text-ink'}
          valueTestId="staff-waiting"
        />
        <AdminStat
          label="Resolved"
          value={summary.resolved}
          hint={`${summary.escalated} escalated`}
          valueTestId="staff-resolved"
        />
        <AdminStat
          label="Avg wait"
          value={duration(summary.avgWaitSec)}
          hint="queue → assigned"
          valueTestId="staff-avg-wait"
        />
        <AdminStat
          label="Avg first reply"
          value={duration(summary.avgFirstResponseSec)}
          hint="opened → first answer"
          valueTestId="staff-avg-response"
        />
        <AdminStat
          label="Busiest hour"
          value={summary.peakHour ?? '—'}
          hint="started sessions, UTC"
          valueTestId="staff-peak"
        />
        <AdminStat
          label="Online"
          value={`${summary.availableStaff}/${summary.staffTotal}`}
          hint={`${summary.staffOnline} signed on${summary.staffTotal === 0 ? ' · no agents yet' : ''}`}
          valueTestId="staff-online"
        />
      </div>

      <LiveMessage message={notice} tone={tone} />

      {/* --- the assistant, as a staff member ----------------------------- */}
      <Panel
        shape="chat"
        testId="staff-bot"
        title="AI assistant"
        subtitle="The assistant takes the first pass on new questions and hands a chat to a person when it is unsure."
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-accent text-on-accent">
              <CheckIcon />
            </span>
            <div className="flex min-w-0 flex-col">
              <span className="text-[14px] font-bold text-ink">
                {bot?.displayName ?? 'Lift2Fit Assistant'}
              </span>
              <span className="text-[11.5px] text-ink-muted">
                {bot
                  ? `${bot.stats.opened} session(s) in ${summary.windowDays}d · ${bot.stats.resolved} resolved`
                  : 'No assistant row yet — add it from the seed script.'}
              </span>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <StatusChip tone={bot?.active ? 'success' : 'neutral'}>
              {bot?.active ? 'Enabled' : 'Disabled'}
            </StatusChip>
            <button
              type="button"
              data-testid="staff-bot-toggle"
              disabled={busy || !bot}
              onClick={() =>
                bot
                  ? void run(`${bot.active ? 'disable' : 'enable'} the assistant`, () =>
                      api.updateSupportStaff(bot.userId, { active: !bot.active }),
                    )
                  : undefined
              }
              className={cn(
                'h-9 rounded-pill border px-4 text-[11px] font-bold uppercase tracking-[0.08em] transition-colors duration-150',
                bot?.active
                  ? 'border-hairline bg-surface text-ink hover:border-danger hover:text-danger'
                  : 'border-accent bg-accent text-on-accent hover:bg-accent-hover',
              )}
            >
              {bot?.active ? 'Disable assistant' : 'Enable assistant'}
            </button>
          </div>
        </div>
        <p className="text-[12px] text-ink-muted">
          Answers come from the help articles (keyword-matched, not generated), so the assistant never
          invents one. A question it cannot answer — or a client asking for a person — goes straight to the
          round-robin queue.
        </p>
      </Panel>

      {/* --- the team ----------------------------------------------------- */}
      <Panel
        shape="chat"
        testId="staff-team"
        title={`Team · ${agents.length + (bot ? 1 : 0)}`}
        subtitle="A shift is the contract window; only a signed-on agent inside it can be assigned a chat."
        actions={
          <button
            type="button"
            data-testid="staff-add-open"
            onClick={() => setAdding((value) => !value)}
            className="h-9 rounded-pill border border-hairline bg-surface px-4 text-[11px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent hover:text-accent"
          >
            {adding ? 'Close' : 'Add staff'}
          </button>
        }
      >
        {adding ? (
          <div className="flex flex-col gap-3 rounded-chat border border-hairline bg-page-subtle p-3" data-testid="staff-add-form">
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Account email">
                <input
                  data-testid="staff-add-email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="agent@lift2fit.com"
                  className="h-10 w-full rounded-chat border border-hairline bg-page px-3 text-[13px] text-ink"
                />
              </Field>
              <Field label="Concurrent chats">
                <input
                  data-testid="staff-add-capacity"
                  value={capacity}
                  onChange={(event) => setCapacity(event.target.value)}
                  inputMode="numeric"
                  className="h-10 w-full rounded-chat border border-hairline bg-page px-3 text-[13px] text-ink"
                />
              </Field>
              <Field label="Shift start (HH:MM)">
                <input
                  data-testid="staff-add-start"
                  value={shiftStart}
                  onChange={(event) => setShiftStart(event.target.value)}
                  placeholder="09:00"
                  className="h-10 w-full rounded-chat border border-hairline bg-page px-3 text-[13px] text-ink"
                />
              </Field>
              <Field label="Shift end (HH:MM)">
                <input
                  data-testid="staff-add-end"
                  value={shiftEnd}
                  onChange={(event) => setShiftEnd(event.target.value)}
                  placeholder="17:00"
                  className="h-10 w-full rounded-chat border border-hairline bg-page px-3 text-[13px] text-ink"
                />
              </Field>
            </div>
            <p className="text-[11.5px] text-ink-muted">
              Staff must already have an account — add them as an agent here after they sign up. The
              assistant is created once and toggled above.
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                data-testid="staff-add-submit"
                disabled={busy || email.trim() === ''}
                onClick={() => void addStaff()}
                className="h-9 rounded-pill bg-accent px-4 text-[11px] font-bold uppercase tracking-[0.08em] text-on-accent hover:bg-accent-hover disabled:opacity-50"
              >
                Add to the team
              </button>
              <AppLinkButton to="/admin/support" variant="ghost" size="sm">
                See the queue
              </AppLinkButton>
            </div>
          </div>
        ) : null}

        {agents.length === 0 ? (
          <p className="text-[13px] text-ink-muted" data-testid="staff-empty">
            No human agents yet. Add an account as staff above, or enable the assistant so help is not
            unowned.
          </p>
        ) : (
          <ul className="flex flex-col gap-2" data-testid="staff-rows">
            {list.data.map((member) => (
              <li key={member.userId}>
                <button
                  type="button"
                  data-testid={`staff-row-${member.userId}`}
                  aria-current={selectedId === member.userId}
                  onClick={() => setSelectedId(member.userId === selectedId ? null : member.userId)}
                  className={cn(
                    'flex w-full flex-wrap items-center justify-between gap-3 rounded-chat border px-3 py-2.5 text-left transition-all duration-150',
                    selectedId === member.userId
                      ? 'border-accent bg-page-subtle shadow-float'
                      : 'border-hairline bg-surface hover:border-accent hover:shadow-float',
                  )}
                >
                  <span className="flex min-w-0 items-center gap-3">
                    <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-ink/5 text-[10px] font-bold uppercase text-ink">
                      {member.displayName.slice(0, 2)}
                    </span>
                    <span className="flex min-w-0 flex-col">
                      <span className="flex flex-wrap items-center gap-1.5">
                        <span className="truncate text-[13.5px] font-bold text-ink">{member.displayName}</span>
                        {member.isBot ? <StatusChip tone="accent">AI</StatusChip> : null}
                        <StatusChip tone={member.available ? 'success' : member.active ? 'neutral' : 'danger'}>
                          {member.isBot
                            ? member.active
                              ? 'On'
                              : 'Off'
                            : member.available
                              ? 'Available'
                              : !member.active
                                ? 'Disabled'
                                : !member.onShift
                                  ? 'Signed out'
                                  : 'Off shift'}
                        </StatusChip>
                      </span>
                      <span className="truncate text-[11.5px] text-ink-muted">
                        {member.email} ·{' '}
                        {member.isBot
                          ? 'always on · takes new questions first'
                          : `${member.shiftStart}–${member.shiftEnd} ${member.timezone}`}
                      </span>
                    </span>
                  </span>
                  <span className="flex flex-wrap items-center gap-4 text-[11px] text-ink-muted">
                    <span className="l2f-tabular font-mono">
                      {member.isBot ? member.stats.opened : member.openChats} open
                    </span>
                    <span className="l2f-tabular font-mono">{member.stats.handled} handled</span>
                    <span className="l2f-tabular font-mono">{duration(member.stats.avgFirstResponseSec)} reply</span>
                  </span>
                </button>

                {selectedId === member.userId ? (
                  <div className="mt-2 flex flex-col gap-3 rounded-chat border border-hairline bg-page-subtle p-3" data-testid="staff-detail">
                    <div className="flex flex-wrap items-center gap-2">
                      {!member.isBot ? (
                        <>
                          <button
                            type="button"
                            data-testid="staff-toggle-active"
                            disabled={busy}
                            onClick={() =>
                              void run(`${member.active ? 'disable' : 'enable'} ${member.displayName}`, () =>
                                api.updateSupportStaff(member.userId, { active: !member.active }),
                              )
                            }
                            className="h-8 rounded-pill border border-hairline bg-surface px-3 text-[10.5px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent"
                          >
                            {member.active ? 'Disable' : 'Enable'}
                          </button>
                          <button
                            type="button"
                            data-testid="staff-toggle-shift"
                            disabled={busy}
                            onClick={() =>
                              void run(`${member.onShift ? 'sign out' : 'sign on'} ${member.displayName}`, () =>
                                api.updateSupportStaff(member.userId, { onShift: !member.onShift }),
                              )
                            }
                            className="h-8 rounded-pill border border-hairline bg-surface px-3 text-[10.5px] font-bold uppercase tracking-[0.08em] text-ink hover:border-accent"
                          >
                            {member.onShift ? 'Sign out' : 'Sign on'}
                          </button>
                          <button
                            type="button"
                            data-testid="staff-remove"
                            disabled={busy}
                            onClick={() =>
                              void run(`remove ${member.displayName}`, () =>
                                api.removeSupportStaff(member.userId),
                              )
                            }
                            className="h-8 rounded-pill border border-hairline bg-surface px-3 text-[10.5px] font-bold uppercase tracking-[0.08em] text-danger hover:border-danger"
                          >
                            Remove
                          </button>
                        </>
                      ) : (
                        <span className="text-[11px] text-ink-muted">
                          The assistant is enabled and disabled from the card above.
                        </span>
                      )}
                    </div>

                    <div className="grid gap-2 sm:grid-cols-4">
                      {[
                        ['Sessions', member.stats.opened],
                        ['Handled', member.stats.handled],
                        ['Resolved', member.stats.resolved],
                        ['Escalated', member.stats.escalated],
                      ].map(([label, value]) => (
                        <div key={String(label)} className="rounded-chat border border-hairline bg-surface px-3 py-2">
                          <div className="text-[9.5px] font-bold uppercase tracking-[0.1em] text-ink-muted">
                            {label}
                          </div>
                          <div className="l2f-tabular font-mono text-[17px] font-bold text-ink">{value}</div>
                        </div>
                      ))}
                    </div>
                    <div className="grid gap-2 sm:grid-cols-3">
                      <div className="text-[11px] text-ink-muted">
                        Avg wait <span className="font-bold text-ink">{duration(member.stats.avgWaitSec)}</span>
                      </div>
                      <div className="text-[11px] text-ink-muted">
                        Avg first reply{' '}
                        <span className="font-bold text-ink">{duration(member.stats.avgFirstResponseSec)}</span>
                      </div>
                      <div className="text-[11px] text-ink-muted">
                        Avg resolution{' '}
                        <span className="font-bold text-ink">{duration(member.stats.avgResolutionSec)}</span>
                      </div>
                    </div>

                    <div className="flex flex-col gap-1">
                      <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">
                        Recent sessions
                      </span>
                      {detail === null ? (
                        <p className="text-[12px] text-ink-muted">Loading…</p>
                      ) : detail.sessions.length === 0 ? (
                        <p className="text-[12px] text-ink-muted">No sessions in this window.</p>
                      ) : (
                        <ul className="flex flex-col gap-1" data-testid="staff-detail-sessions">
                          {detail.sessions.map((row) => (
                            <li
                              key={row.sessionId}
                              className="flex flex-wrap items-center justify-between gap-2 border-t border-hairline py-1.5 text-[12px] text-ink-muted"
                            >
                              <span className="min-w-0 truncate text-ink">{row.clientName}</span>
                              <span className="flex flex-wrap items-center gap-2">
                                <StatusChip tone={row.status === 'closed' ? 'neutral' : 'accent'}>
                                  {row.status}
                                </StatusChip>
                                <span>{row.resolution ?? 'open'}</span>
                                <span className="l2f-tabular font-mono">
                                  {duration(row.waitSeconds)} wait · {duration(row.handleSeconds)} handled
                                </span>
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </Panel>

      <p className="flex items-start gap-2 text-[11.5px] text-ink-muted">
        <ShieldIcon className="mt-0.5 text-accent" />
        Assignment is round-robin among enabled, signed-on agents inside their shift window, least
        recently assigned first. When nobody is free the client is queued and told their place; the
        alert above appears here.
      </p>
    </div>
  );
}
